import React, { useCallback, useEffect, useState } from 'react';
import {
  Banner,
  EmptyState,
  PageContainer,
  PageHeader,
  Select,
  Spinner,
  Tabs,
  useToast,
} from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useT } from '../../i18n';
import { settingsApi } from './settingsApi';
import { useProperty } from '../../app/PropertyContext';
import CommercialTermsForm from './CommercialTermsForm';
import PropertiesTab from './PropertiesTab';
import LocationsTab from './LocationsTab';
import ApprovalWorkflowsTab from './ApprovalWorkflowsTab';
import RequisitionSettingsTab from './RequisitionSettingsTab';
import ReceivingSettingsTab from './ReceivingSettingsTab';
import InvoiceMatchingSettingsTab from './InvoiceMatchingSettingsTab';
import CodingTab from './CodingTab';
import './settings.css';

const TABS = [
  { id: 'properties', label: 'Properties' },
  { id: 'locations', label: 'Locations' },
  { id: 'org', label: 'Organization defaults' },
  { id: 'property', label: 'Property overrides' },
  { id: 'approvals', label: 'Approval workflows' },
  { id: 'requisitions', label: 'Requisitions' },
  { id: 'receiving', label: 'Receiving' },
  { id: 'matching', label: 'Invoice matching' },
  { id: 'coding', label: 'Coding' },
];

export default function SettingsPage() {
  const toast = useToast();
  const t = useT();
  const { properties, loaded: propsLoaded, reload: reloadProperties, refreshCommercial } = useProperty();
  const [tab, setTab] = useState('properties');

  const [org, setOrg] = useState(null);
  const [orgLoading, setOrgLoading] = useState(true);
  const [orgSaving, setOrgSaving] = useState(false);

  const [selectedProperty, setSelectedProperty] = useState('');
  const [propSettings, setPropSettings] = useState(null);
  const [propLoading, setPropLoading] = useState(false);
  const [propSaving, setPropSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setOrg(await settingsApi.getOrg());
      } catch (err) {
        toast.error(t('Could not load settings'), { description: err?.message });
      } finally {
        setOrgLoading(false);
      }
    })();
  }, [toast, t]);

  const loadProperty = useCallback(async (propertyId) => {
    setSelectedProperty(propertyId);
    setPropSettings(null);
    if (!propertyId) return;
    setPropLoading(true);
    try {
      setPropSettings(await settingsApi.getPropertyCommercial(propertyId));
    } catch (err) {
      toast.error(t('Could not load property settings'), { description: err?.message });
    } finally {
      setPropLoading(false);
    }
  }, [toast, t]);

  const handleSaveOrg = async (payload) => {
    setOrgSaving(true);
    try {
      const res = await settingsApi.updateOrg(payload);
      setOrg(res);
      refreshCommercial(); // apply new currency list / number format app-wide immediately
      toast.success(t('Organization defaults saved'));
    } catch (err) {
      toast.error(t('Could not save settings'), { description: err?.message });
    } finally {
      setOrgSaving(false);
    }
  };

  const handleSaveProperty = async (payload) => {
    if (!selectedProperty) return;
    setPropSaving(true);
    try {
      const res = await settingsApi.updatePropertyCommercial(selectedProperty, { propertyId: selectedProperty, ...payload });
      setPropSettings(res);
      refreshCommercial();
      toast.success(t('Property overrides saved'));
    } catch (err) {
      toast.error(t('Could not save overrides'), { description: err?.message });
    } finally {
      setPropSaving(false);
    }
  };

  return (
    <PageContainer size="full">
      <div className="set-page">
        <PageHeader
          title={t('Settings')}
          description={t('Commercial standards used across suppliers and purchasing. Configure organization defaults and optional per-property overrides.')}
          icon={<Icon.Settings size={22} />}
          tabs={<Tabs tabs={TABS.map((tb) => ({ ...tb, label: t(tb.label) }))} value={tab} onChange={setTab} ariaLabel={t('Settings sections')} />}
        />

        {tab === 'properties' && (
          <PropertiesTab
            properties={properties}
            loading={!propsLoaded}
            onChanged={reloadProperties}
          />
        )}

        {tab === 'locations' && <LocationsTab />}

        {tab === 'approvals' && <ApprovalWorkflowsTab />}

        {tab === 'requisitions' && <RequisitionSettingsTab />}

        {tab === 'receiving' && <ReceivingSettingsTab />}

        {tab === 'matching' && <InvoiceMatchingSettingsTab />}

        {tab === 'coding' && <CodingTab />}

        {tab === 'org' && (
          orgLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-6)' }}><Spinner size={24} /></div>
          ) : (
            <CommercialTermsForm key="org" mode="org" initial={org} saving={orgSaving} onSave={handleSaveOrg} />
          )
        )}

        {tab === 'property' && (
          !propsLoaded ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-6)' }}><Spinner size={24} /></div>
          ) : properties.length === 0 ? (
            <EmptyState
              icon={<Icon.Building size={28} />}
              title={t('No properties yet')}
              description={t('Properties sync from Prostay and TableView. Once a property exists here, you can override the organization commercial defaults for it.')}
            />
          ) : (
            <>
              <div className="set-property-pick">
                <Select
                  options={[{ value: '', label: t('Select a property…') }, ...properties.map((p) => ({ value: p.id, label: p.name }))]}
                  value={selectedProperty}
                  onChange={loadProperty}
                  ariaLabel={t('Property')}
                  searchable
                />
              </div>

              {!selectedProperty ? (
                <Banner tone="info">{t('Choose a property to view or override its commercial standards. Sections left as “inherit” use the organization defaults.')}</Banner>
              ) : propLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-6)' }}><Spinner size={24} /></div>
              ) : propSettings ? (
                <CommercialTermsForm
                  key={selectedProperty}
                  mode="property"
                  initial={propSettings}
                  inheritFrom={org}
                  saving={propSaving}
                  onSave={handleSaveProperty}
                />
              ) : null}
            </>
          )
        )}
      </div>
    </PageContainer>
  );
}
